import * as Sentry from '@sentry/react';

/**
 * Initialise Sentry uniquement en production, avec intégrations adaptées à CRESS Guadeloupe.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('⚠️ Sentry DSN non configuré. Suivi des erreurs désactivé.');
    return;
  }

  // ✅ Active seulement en production
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true, // protège les données personnelles
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: 0.2, // 20 % des sessions
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        const error = hint.originalException;

        // 🚫 Filtrer certaines erreurs bruitées
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message).toLowerCase();
          if (
            message.includes('extension') ||
            message.includes('chrome-extension') ||
            message.includes('moz-extension') ||
            message.includes('webkit-masked-url')
          ) {
            return null;
          }
        }

        // ✅ Ajout automatique du contexte CRESS si disponible
        if (event.user && event.user.profile_type) {
          event.tags = {
            ...event.tags,
            user_profile: event.user.profile_type,
          };
        }

        return event;
      },
    });
    console.info('✅ Sentry initialisé en production');
  }
}

/**
 * Enregistre l'utilisateur connecté (depuis Supabase Auth).
 * @param user Informations utilisateur Supabase.
 */
export function setUserContext(user: { id: string; email: string; profile_type?: string }) {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.email.split('@')[0],
      role: user.profile_type ?? 'UNKNOWN',
    });
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `User connected: ${user.email}`,
      level: 'info',
    });
  }
}

/**
 * Supprime le contexte utilisateur (lors de la déconnexion).
 */
export function clearUserContext() {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(null);
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `User disconnected`,
      level: 'info',
    });
  }
}

/**
 * Tagge l'ID du projet ESS en cours.
 */
export function setProjectContext(projectId: string | null) {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    if (projectId) {
      Sentry.setTag('project_id', projectId);
      Sentry.addBreadcrumb({
        category: 'project',
        message: `Switched to project ${projectId}`,
        level: 'info',
      });
    } else {
      Sentry.setTag('project_id', null);
    }
  }
}

/**
 * Capture une exception avec contexte additionnel.
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, { extra: context });
  } else {
    console.error('⚙️ [Dev] Error captured:', error, context);
  }
}

/**
 * Capture un message d'information, d'avertissement ou d'erreur.
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(message, level);
  } else {
    console.log(`[${level.toUpperCase()}] ${message}`);
  }
}

/**
 * Ajoute un fil d’exécution (breadcrumb) pour suivre le contexte utilisateur.
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.addBreadcrumb(breadcrumb);
  }
}

// Export direct de Sentry si besoin ailleurs
export { Sentry };
